/*
  PCG slab renderer (storefront slab gallery, spec §6.3 R13–R16, R22, D27, D30).

  Draws one gallery slab in the browser: the slab frame, the catalog card image in the card
  window, the plastic over the card, and the PCG label as inline SVG. Dependency-free: no
  framework, no network calls of its own (it only sets the image URLs it is given), no fonts
  of its own (Arial or Helvetica from the visitor's system). It scales with its container.

  window.PCGSlab = {
    version: '1.0.0',
    render(slab, opts) -> HTMLAnchorElement
  }

  slab  one Slab from GET /public/api/v1/gallery (spec R4): gradedId, nameLine, detailLine,
        gradeWord, gradeNumber, labelStyle ('gold' | 'black-gold' | 'black-silver'),
        subgrades { centering, corners, edges, surface } (strings, "O/C" possible),
        imageUrl, crop { x, y, scale }. `slab` (the slab type) may be null and is not read:
        every slab uses the standard frame for now (D14).
  opts  frameUrl  the frame art (assets/pcg-slab-frame.webp, lossless); required.
        glassUrl  optional overlay image drawn over the card, on top of the CSS glass
                  (.pcg-slab__glass). None ships: the Figma plastic over the card is a flat
                  tint that CSS reproduces exactly (spec §3), so this is only a hook.
        href      optional link; defaults to the slab's pop-report cert page (D10).

  The returned <a> opens in a new tab, carries data-graded-id and an accessible name
  ("Pikachu, Gem Mint 10, cert 000413661"); the frame and card images have empty alt text and
  the label SVG is aria-hidden (R16). Its data-image attribute goes loading -> loaded | error.
  When the card image fails, or there is no image URL, the <a> dispatches a bubbling
  `pcgslab:imageerror` event, detail { gradedId, imageUrl }. The card image loads eagerly on
  purpose: the gallery section counts an image that has not loaded within its 8-second budget
  as failed (R20), and a lazy image below the fold would never start.

  The Hub vendors this exact file with the same version string (D23). Change it here first,
  bump the version, and copy it across.

  Label geometry is ported from pop-report src/lib/labelLayout.ts and labelArt.ts: the 385×116
  label, the 6-unit inset frame with the PCG gap, the arc artwork, the grade box at x 309 and
  every text line's position. Type sizes, colours, the grade face and the arc size follow the
  Figma slabs this replaces (D27).
*/
(function () {
  if (window.PCGSlab) return;

  const VERSION = '1.0.0';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const CERT_BASE = 'https://pop-report.web.app/report/';

  /* ── Geometry (pop-report labelLayout.ts, Figma-tuned) ────────────────────────────── */

  const LW = 385;
  const LH = 116;
  const BORDER_INSET = 6;
  const GRADE_X = 309;
  const ARC_BASE_Y = 99;
  /* Arcs 8% wider and spread 3.2% further about the same middle than the pop report's
     (55.5/123.83/192.17/260.5 at 56×29), as measured off the Figma exports. */
  const ARC_CX = [52.2, 122.7, 193.3, 263.8];
  const ARC_SCALED_W = 60.5;
  const ARC_SCALED_H = 30;
  const ARC_ORIG_W = 239.93761;
  const ARC_ORIG_H = 119.9987;
  const NAME_SIZE = 22;
  const DETAIL_SIZE = 12;
  const SUB_SIZE = 17;
  const CAPTION_SIZE = 6.5;
  const GRADE_WORD_SIZE = 13;
  const FIGURE_SIZE = 40;
  const LOGO_GAP_END = 34;
  const NAME_MAX_W = GRADE_X - BORDER_INSET - 8;
  const GRADE_MAX_W = LW - GRADE_X - BORDER_INSET - 4;
  const GCX = (GRADE_X + LW - BORDER_INSET) / 2;
  const CCX = (BORDER_INSET + GRADE_X) / 2;
  const GRADE_BOX = { x: GRADE_X, y: BORDER_INSET, w: LW - GRADE_X - BORDER_INSET, h: LH - 2 * BORDER_INSET };
  const FRONT_LOGO_TRANSFORM = 'translate(1.1, 1.1) scale(1.8)';
  /* R14: a line too wide shrinks down to 60% of its base size, then compresses horizontally. */
  const MIN_SCALE = 0.6;

  /* ── Artwork (pop-report labelArt.ts, verbatim) ───────────────────────────────────── */

  const TRACK =
    'm 239.93761,119.98405 -13.97518,-0.0293 C 225.97533,53.748199 173.03297,13.949667 119.97067,13.991954 66.908366,14.034242 14.006454,53.667889 13.900251,119.95259 L 0,119.99869 C 0.0036919,40.637517 59.974526,-0.27240668 119.96952,0.00136504 179.96453,0.27513675 239.8819,40.794105 239.93761,119.98405 Z';
  const ARC_SUBGRADE_PATHS = {
    1: TRACK + ' M 7.0896606,83.345156 c 4.1867004,1.15225 5.5371184,1.743947 9.3066274,2.873047 8.227918,-36.006659 49.701264,-75.040941 103.574102,-75.008819 56.62285,-0.71788 108.47284,41.062349 108.98645,104.752166 3.63604,-0.006 4.7154,-0.0187 7.99859,0 C 233.5909,41.082774 178.26001,2.5456633 119.96883,2.9890767 61.677657,3.4324901 23.718927,34.546153 7.0896606,83.345156 Z',
    2: TRACK + ' M 23.619991,50.513049 c 2.073343,1.353995 5.968048,3.626163 7.646948,4.730676 3.658597,-7.020523 34.830613,-44.066463 88.703451,-44.034341 56.62285,-0.71788 106.35152,41.062349 108.98645,104.752166 3.63604,-0.006 4.7154,-0.0187 7.99859,0 C 233.3409,41.082774 178.26001,2.5456633 119.96883,2.9890767 61.677657,3.4324901 34.76918,35.124842 23.619991,50.513049 Z',
    3: TRACK + ' M 50.337434,24.224055 c 1.698343,2.353995 2.6986,3.720487 5.5025,7.2 8.978141,-5.458023 28.465618,-20.246793 64.130456,-20.214671 56.62285,-0.71788 107.70507,41.769456 108.98645,104.752166 3.63604,-0.006 4.7154,-0.0187 7.99859,0 C 234.75511,43.557648 178.26001,2.5456633 119.96883,2.9890767 86.426394,4.669927 66.259593,12.9901 50.337434,24.224055 Z',
    4: TRACK + ' M 83.476473,8.1391564 c 0.991236,2.9285196 1.854095,5.1168226 2.846034,8.1322966 6.26583,-2.707261 24.339273,-5.005803 33.647923,-5.062069 56.62285,-0.71788 106.88181,41.592679 108.98641,104.752166 3.63604,-0.006 4.7154,-0.0187 7.99859,0 C 233.16412,41.789881 178.26001,2.5456633 119.96883,2.9890767 107.36389,2.794927 96.086132,3.9052012 83.476473,8.1391564 Z',
    5: TRACK + ' M 119.97043,11.209384 c 56.62285,-0.71788 106.61664,41.599241 109.0748,104.758726 3.63604,-0.006 4.40604,0.0252 7.68923,0.0439 C 234.00381,40.779677 178.26001,2.5456633 119.96883,2.9890767 Z',
    6: TRACK + ' M 153.68868,16.549805 c 26.1547,6.850744 72.62955,35.529728 75.14316,99.469545 3.63604,-0.006 4.74665,0.0122 8.02984,0.031 C 232.36847,40.317971 184.43502,16.989139 156.27743,8.1078045 Z',
    7: TRACK + ' M 184.53693,30.881805 c 27.64582,22.514326 41.56486,46.797444 44.29491,85.043795 3.63604,-0.006 4.87165,0.0747 8.15484,0.0935 -1.07532,-43.141127 -20.02263,-74.695869 -47.64975,-91.649795 z',
    7.5: TRACK + ' M 197.79493,42.021305 c 14.93503,16.30354 29.12381,35.02498 31.16191,73.991005 3.63604,-0.006 4.62701,-0.019 7.9102,-3e-4 -1.42887,-42.964304 -19.69399,-68.661779 -33.19611,-80.115705 z',
    8: TRACK + ' M 209.21658,55.332254 c 8.65946,16.922258 17.43699,23.658575 19.74026,60.680056 3.63604,-0.006 4.62701,-0.019 7.9102,-3e-4 -1.42887,-42.964304 -16.62266,-58.22048 -20.86611,-65.785319 z',
    8.5: TRACK + ' M 218.08043,69.995805 c 6.98008,14.712549 10.69446,28.617237 10.87641,46.016505 3.63604,-0.006 4.62701,-0.019 7.9102,-3e-4 -0.89854,-21.309159 -5.80362,-39.467327 -11.19611,-49.772205 z',
    9: TRACK + ' M 224.50545,85.968613 c 2.20711,7.420511 3.73911,12.821206 4.45139,30.043697 3.63604,-0.006 4.62701,-0.019 7.9102,-3e-4 -0.54499,-12.38194 -2.02207,-23.713014 -5.20485,-32.382707 z',
    9.5: TRACK + ' M 227.24455,98.644758 c 1.11336,6.108002 1.46876,9.176302 1.71229,17.367552 3.63604,-0.006 4.62701,-0.019 7.9102,-3e-4 -0.13874,-8.28819 -0.95436,-14.08513 -2.02287,-19.036066 z',
    10: TRACK
  };
  const ARC_KEYS = Object.keys(ARC_SUBGRADE_PATHS).map(Number).sort((a, b) => a - b);
  const CORNER_LOGO_P = 'M4.06,6.09H5.27A1.66,1.66,0,0,0,6.8,5.28v0A2.53,2.53,0,0,0,9.45,7.87a2.33,2.33,0,0,0,2.21-1.34L10.55,6a1.2,1.2,0,0,1-1.1.73A1.37,1.37,0,0,1,8.1,5.3,1.36,1.36,0,0,1,9.45,3.86a1.21,1.21,0,0,1,1.1.72l1.11-.52A2.31,2.31,0,0,0,9.45,2.72,2.56,2.56,0,0,0,7,4.28,1.6,1.6,0,0,0,5.27,2.81H2.78v5H4.06Zm0-2.2h1A.56.56,0,1,1,5.09,5h-1Z';
  const CORNER_LOGO_C = 'M14.06,7.87a2.88,2.88,0,0,0,2.2-1V5h-2.4V6H15v.42a1.57,1.57,0,0,1-.94.3A1.38,1.38,0,0,1,12.67,5.3a1.37,1.37,0,0,1,1.39-1.44,1.23,1.23,0,0,1,1,.59l1.08-.57a2.35,2.35,0,0,0-2.13-1.16A2.55,2.55,0,0,0,11.37,5.3,2.54,2.54,0,0,0,14.06,7.87Z';

  /*
    The three label styles (D7, D27), coloured from the Figma exports' pixels:
      gold          Flawless: the Rayquaza label (brushed gold face, white lettering, silver grade box)
      black-gold    above 9, not Flawless: the Boa Hancock label (black face, gold lettering)
      black-silver  9 and below: the Mox Ruby label (black face, silver lettering)
    Each paint is a colour or a gradient { dir, stops: [[offset, colour], ...] } over the
    shape's own box. brush / panelBrush are brushed-streak strengths (0 none).
  */
  const grad = (dir, stops) => ({ dir, stops });
  const STYLES = {
    gold: {
      face: grad('x', [[0, '#B79458'], [0.55, '#BC9E60'], [1, '#C0A666']]), brush: 0.34,
      frame: '#D4D4D4', arc: '#D4D4D4', mark: '#F0F0F0', name: '#F2F2F2', detail: '#F2F2F2',
      sub: '#E8E8E8', caption: '#E3E2E2',
      panel: '#D6D6D6', panelBrush: 0, gradeInk: '#C3A452'
    },
    'black-gold': {
      face: '#303030', brush: 0,
      frame: grad('y', [[0, '#E8CC81'], [0.04, '#D4AF5F'], [0.96, '#D4AF5F'], [1, '#D9BE79']]),
      arc: grad('y', [[0, '#E8CD86'], [0.45, '#D2AF61'], [1, '#C59D50']]),
      mark: '#D2AE5E', name: '#D6B56A', detail: '#C9AB67', sub: '#C5A55E', caption: '#BEA05C',
      panel: grad('x', [[0, '#D5B060'], [0.5, '#CFA859'], [1, '#C29B4C']]), panelBrush: 0.2,
      gradeInk: '#303030'
    },
    'black-silver': {
      face: '#303030', brush: 0,
      frame: grad('y', [[0, '#E1E1E1'], [0.04, '#9A9A9A'], [0.96, '#9A9A9A'], [1, '#AFAFAF']]),
      arc: grad('y', [[0, '#DADADA'], [0.45, '#B6B6B6'], [1, '#A2A2A2']]),
      mark: '#9E9E9E', name: grad('y', [[0, '#C6C6C6'], [1, '#B4B4B4']]), detail: '#B0B0B0',
      sub: '#A7A7A7', caption: '#A3A3A3',
      panel: grad('y', [[0, '#D0D0D0'], [0.5, '#C3C3C3'], [1, '#9C9C9C']]), panelBrush: 0.3,
      gradeInk: '#303030'
    }
  };

  const SUBGRADES = [
    ['centering', 'CENTERING'], ['corners', 'CORNERS'], ['edges', 'EDGES'], ['surface', 'SURFACE']
  ];

  /* ── Data helpers ─────────────────────────────────────────────────────────────────── */

  const text = (value) => (typeof value === 'string' ? value.trim() : typeof value === 'number' && isFinite(value) ? String(value) : '');
  const round2 = (value) => Math.round(value * 100) / 100;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const number = (value, fallback) => {
    const n = typeof value === 'number' ? value : parseFloat(value);
    return isFinite(n) ? n : fallback;
  };

  /* The feed names the style (§5.5); the same rule stands in when it does not. */
  function styleFor(slab) {
    const named = text(slab.labelStyle).toLowerCase();
    if (STYLES[named]) return named;
    if (text(slab.gradeWord).toLowerCase() === 'flawless') return 'gold';
    return number(slab.gradeNumber, 0) > 9 ? 'black-gold' : 'black-silver';
  }

  function arcPathFor(score) {
    if (!(score > 0)) return '';
    const rounded = Math.min(10, Math.round(score * 2) / 2);
    if (ARC_SUBGRADE_PATHS[rounded]) return ARC_SUBGRADE_PATHS[rounded];
    const below = ARC_KEYS.filter((k) => k <= rounded);
    return ARC_SUBGRADE_PATHS[below.length ? below[below.length - 1] : ARC_KEYS[0]];
  }

  function arcTransform(i) {
    const sx = ARC_SCALED_W / ARC_ORIG_W;
    const sy = ARC_SCALED_H / ARC_ORIG_H;
    return 'translate(' + (ARC_CX[i] - ARC_SCALED_W / 2) + ', ' + (ARC_BASE_Y - ARC_SCALED_H) + ') scale(' + sx + ', ' + sy + ')';
  }

  function framePath() {
    const i = BORDER_INSET;
    const g = LOGO_GAP_END;
    return 'M ' + g + ',' + i + ' H ' + (LW - i) + ' V ' + (LH - i) + ' H ' + i + ' V 19 H ' + (i + 2) +
      ' V ' + (LH - i - 2) + ' H ' + (LW - i - 2) + ' V ' + (i + 2) + ' H ' + g + ' Z';
  }

  /* ── Text fitting (R14) ───────────────────────────────────────────────────────────── */

  const FONT_FAMILY = 'Arial, Helvetica, "Hiragino Sans", "Hiragino Kaku Gothic ProN", "Yu Gothic", Meiryo, sans-serif';
  const FONT_WEIGHT = { regular: 400, bold: 700 };

  /* Fallback advance widths in em (pop-report estimateTextWidth), for a browser without canvas. */
  const NARROW = "il.,:;'!|Ijft()[]/ ";
  const WIDE = 'mwMW@%';
  const ADVANCE = {
    regular: { narrow: 0.269, wide: 0.873, upper: 0.676, other: 0.541 },
    bold: { narrow: 0.302, wide: 0.885, upper: 0.688, other: 0.569 }
  };

  function isFullWidth(code) {
    return (code >= 0x1100 && code <= 0x115f) || (code >= 0x2e80 && code <= 0xa4cf) ||
      (code >= 0xac00 && code <= 0xd7a3) || (code >= 0xf900 && code <= 0xfaff) ||
      (code >= 0xfe30 && code <= 0xfe4f) || (code >= 0xff00 && code <= 0xff60) ||
      (code >= 0xffe0 && code <= 0xffe6) || code >= 0x20000;
  }

  function estimateWidth(chars, fontSize, weight) {
    const table = ADVANCE[weight];
    let em = 0;
    chars.forEach((ch) => {
      const code = ch.codePointAt(0) || 0;
      if (isFullWidth(code)) em += 1;
      else if (NARROW.indexOf(ch) >= 0) em += table.narrow;
      else if (WIDE.indexOf(ch) >= 0) em += table.wide;
      else if (ch >= 'A' && ch <= 'Z') em += table.upper;
      else em += table.other;
    });
    return em * fontSize;
  }

  /* Measured with the visitor's own font, which is the one the SVG will draw with. */
  let measureContext;
  function measureWidth(str, fontSize, weight, letterSpacing) {
    if (measureContext === undefined) {
      try {
        measureContext = document.createElement('canvas').getContext('2d');
      } catch (error) {
        measureContext = null;
      }
    }
    const chars = Array.from(str);
    const spacing = letterSpacing * Math.max(0, chars.length - 1);
    if (!measureContext) return estimateWidth(chars, fontSize, weight) + spacing;
    measureContext.font = FONT_WEIGHT[weight] + ' ' + fontSize + 'px ' + FONT_FAMILY;
    return measureContext.measureText(str).width + spacing;
  }

  /* A line wider than its room shrinks to fit, down to MIN_SCALE of its size; past that it is
     squeezed with textLength. It never wraps or truncates. */
  function fit(spec) {
    const ls = spec.letterSpacing || 0;
    const out = Object.assign({}, spec, { letterSpacing: ls });
    if (!spec.maxWidth) return out;
    const width = measureWidth(spec.text, spec.fontSize, spec.weight, ls);
    if (width <= spec.maxWidth) return out;
    const factor = Math.max(MIN_SCALE, spec.maxWidth / width);
    out.fontSize = round2(spec.fontSize * factor);
    out.letterSpacing = round2(ls * factor);
    out.textLength = spec.maxWidth;
    return out;
  }

  /* ── SVG building ─────────────────────────────────────────────────────────────────── */

  let uid = 0;

  function el(name, attrs, parent) {
    const node = document.createElementNS(SVG_NS, name);
    Object.keys(attrs || {}).forEach((k) => {
      if (attrs[k] !== undefined && attrs[k] !== null) node.setAttribute(k, attrs[k]);
    });
    if (parent) parent.appendChild(node);
    return node;
  }

  function gradient(defs, id, spec) {
    const g = el('linearGradient', {
      id, x1: 0, y1: 0, x2: spec.dir === 'x' ? 1 : 0, y2: spec.dir === 'x' ? 0 : 1
    }, defs);
    spec.stops.forEach((s) => el('stop', { offset: s[0], 'stop-color': s[1] }, g));
  }

  /* Fractal noise stretched along x, greyed and soft-lit over the fill (pop-report Face). */
  function brushFilter(defs, id, slope) {
    const intercept = (1 - slope) / 2;
    const f = el('filter', { id, x: 0, y: 0, width: 1, height: 1, 'color-interpolation-filters': 'sRGB' }, defs);
    el('feTurbulence', { type: 'fractalNoise', baseFrequency: '0.011 0.9', numOctaves: 2, seed: 7, result: 'noise' }, f);
    el('feColorMatrix', { in: 'noise', type: 'saturate', values: 0, result: 'grey' }, f);
    const t = el('feComponentTransfer', { in: 'grey', result: 'streaks' }, f);
    ['feFuncR', 'feFuncG', 'feFuncB'].forEach((fn) => el(fn, { type: 'linear', slope, intercept }, t));
    el('feBlend', { in: 'streaks', in2: 'SourceGraphic', mode: 'soft-light', result: 'brushed' }, f);
    el('feComposite', { in: 'brushed', in2: 'SourceGraphic', operator: 'in' }, f);
  }

  /* embolden: a stroke in the fill's own paint, for Figma's heavier-than-Arial-Bold lettering. */
  function drawText(parent, spec, fill, embolden) {
    const t = el('text', {
      x: spec.x,
      y: spec.y,
      'font-size': spec.fontSize,
      'font-family': FONT_FAMILY,
      'font-weight': FONT_WEIGHT[spec.weight],
      'letter-spacing': spec.letterSpacing || undefined,
      'text-anchor': spec.anchor,
      textLength: spec.textLength,
      lengthAdjust: spec.textLength ? 'spacingAndGlyphs' : undefined,
      fill,
      stroke: embolden ? fill : undefined,
      'stroke-width': embolden ? round2(spec.fontSize * embolden) : undefined,
      'stroke-linejoin': embolden ? 'round' : undefined
    }, parent);
    t.textContent = spec.text;
    return t;
  }

  function buildLabel(slab, styleName) {
    const style = STYLES[styleName];
    const id = 'pcg-slab-' + (++uid);
    const svg = el('svg', {
      viewBox: '0 0 ' + LW + ' ' + LH,
      preserveAspectRatio: 'none',
      class: 'pcg-slab__label',
      'aria-hidden': 'true',
      focusable: 'false',
      'data-label-style': styleName
    });
    const defs = el('defs', {}, svg);
    if (style.brush) brushFilter(defs, id + '-brush', style.brush);
    if (style.panelBrush) brushFilter(defs, id + '-pbrush', style.panelBrush);
    /* A style entry as a fill: a colour as is, a gradient as a url to its own <linearGradient>. */
    const paint = (key) => {
      const v = style[key];
      if (typeof v === 'string') return v;
      gradient(defs, id + '-' + key, v);
      return 'url(#' + id + '-' + key + ')';
    };

    el('rect', {
      x: 0.25, y: 0.25, width: LW - 0.5, height: LH - 0.5, rx: 4,
      fill: paint('face'), filter: style.brush ? 'url(#' + id + '-brush)' : undefined
    }, svg);

    el('rect', {
      x: GRADE_BOX.x, y: GRADE_BOX.y, width: GRADE_BOX.w, height: GRADE_BOX.h,
      fill: paint('panel'), filter: style.panelBrush ? 'url(#' + id + '-pbrush)' : undefined
    }, svg);

    const word = text(slab.gradeWord);
    const figure = text(slab.gradeNumber);
    if (word) {
      drawText(svg, fit({ text: word, x: GCX, y: 44, fontSize: GRADE_WORD_SIZE, weight: 'bold', anchor: 'middle', letterSpacing: -0.3, maxWidth: GRADE_MAX_W }), style.gradeInk, 0.025);
    }
    if (figure) {
      drawText(svg, fit({ text: figure, x: GCX, y: 80, fontSize: FIGURE_SIZE, weight: 'bold', anchor: 'middle', letterSpacing: -1.5, maxWidth: GRADE_MAX_W }), style.gradeInk, 0.02);
    }

    el('path', { d: framePath(), fill: paint('frame') }, svg);
    const mark = el('g', { transform: FRONT_LOGO_TRANSFORM }, svg);
    const markInk = paint('mark');
    el('path', { d: CORNER_LOGO_P, fill: markInk }, mark);
    el('path', { d: CORNER_LOGO_C, fill: markInk }, mark);

    const name = text(slab.nameLine);
    const detail = text(slab.detailLine);
    if (name) {
      drawText(svg, fit({ text: name, x: CCX, y: 41, fontSize: NAME_SIZE, weight: 'bold', anchor: 'middle', maxWidth: NAME_MAX_W }), paint('name'), 0.03);
    }
    if (detail) {
      drawText(svg, fit({ text: detail, x: CCX, y: 57, fontSize: DETAIL_SIZE, weight: 'regular', anchor: 'middle', maxWidth: NAME_MAX_W }), paint('detail'));
    }

    const subgrades = slab.subgrades || {};
    const subInk = paint('sub');
    const captionInk = paint('caption');
    const arcInk = paint('arc');
    SUBGRADES.forEach(([key, caption], i) => {
      const value = text(subgrades[key]);
      /* "O/C" prints where the number would go, over the empty arc track (label station rule). */
      const offCenter = value.toUpperCase() === 'O/C';
      const score = offCenter ? 0 : number(value, 0);
      const g = el('g', {}, svg);
      const shown = offCenter ? 'O/C' : score > 0 ? value : '';
      if (shown) {
        drawText(g, fit({ text: shown, x: ARC_CX[i], y: 92, fontSize: SUB_SIZE, weight: 'regular', anchor: 'middle', maxWidth: ARC_SCALED_W }), subInk);
      }
      drawText(g, fit({ text: caption, x: ARC_CX[i], y: 99, fontSize: CAPTION_SIZE, weight: 'regular', anchor: 'middle' }), captionInk);
      const d = offCenter ? TRACK : arcPathFor(score);
      if (d) el('path', { d, transform: arcTransform(i), fill: arcInk }, g);
    });
    return svg;
  }

  /* ── Public API ───────────────────────────────────────────────────────────────────── */

  function render(slab, opts) {
    slab = slab || {};
    opts = opts || {};
    const gradedId = text(slab.gradedId);
    const imageUrl = text(slab.imageUrl);
    const name = text(slab.nameLine);
    const grade = [text(slab.gradeWord), text(slab.gradeNumber)].filter(Boolean).join(' ');

    const a = document.createElement('a');
    a.className = 'pcg-slab';
    a.href = opts.href || CERT_BASE + encodeURIComponent(gradedId);
    a.target = '_blank';
    a.rel = 'noopener';
    a.setAttribute('data-graded-id', gradedId);
    a.setAttribute('data-image', 'loading');
    a.setAttribute('aria-label', name + ', ' + grade + ', cert ' + gradedId);

    /* The Figma exports' own plastic (D30, R22). */
    const frame = document.createElement('img');
    frame.className = 'pcg-slab__frame';
    frame.alt = '';
    frame.decoding = 'async';
    if (opts.frameUrl) frame.src = opts.frameUrl;
    a.appendChild(frame);

    const win = document.createElement('div');
    win.className = 'pcg-slab__window';
    const card = document.createElement('img');
    card.className = 'pcg-slab__card';
    card.alt = '';
    card.decoding = 'async';
    card.referrerPolicy = 'no-referrer';
    /* crop.x / crop.y are fractions of the window, crop.scale a zoom about its middle (R11). */
    const crop = slab.crop || {};
    const cx = clamp(number(crop.x, 0), -0.5, 0.5);
    const cy = clamp(number(crop.y, 0), -0.5, 0.5);
    const cs = clamp(number(crop.scale, 1), 0.8, 2);
    if (cx || cy || cs !== 1) {
      card.style.transform = 'translate(' + round2(cx * 100) + '%, ' + round2(cy * 100) + '%) scale(' + cs + ')';
    }

    let settled = false;
    const failed = () => {
      if (settled) return;
      settled = true;
      a.setAttribute('data-image', 'error');
      a.dispatchEvent(new CustomEvent('pcgslab:imageerror', { bubbles: true, detail: { gradedId, imageUrl } }));
    };
    card.addEventListener('load', () => {
      if (settled) return;
      settled = true;
      a.setAttribute('data-image', 'loaded');
    });
    card.addEventListener('error', failed);
    /* No URL: fail on the next task, once the caller has put the slab in the page. */
    if (imageUrl) card.src = imageUrl;
    else setTimeout(failed, 0);
    win.appendChild(card);

    /* The plastic over the card: see .pcg-slab__glass. */
    const glass = document.createElement('div');
    glass.className = 'pcg-slab__glass';
    if (opts.glassUrl) glass.style.backgroundImage = 'url(' + JSON.stringify(String(opts.glassUrl)) + ')';
    win.appendChild(glass);
    a.appendChild(win);

    const labelBox = document.createElement('div');
    labelBox.className = 'pcg-slab__labelbox';
    labelBox.appendChild(buildLabel(slab, styleFor(slab)));
    a.appendChild(labelBox);
    return a;
  }

  window.PCGSlab = { version: VERSION, render };
})();
